export type DynamicWidgetExpectedState =
    | {
          readonly shift_statuses_by_id: Record<string, string>;
          readonly shift_statuses?: never;
      }
    | {
          readonly shift_statuses: Array<string>;
          readonly shift_statuses_by_id?: never;
      };

export type DynamicWidgetActionRequest = {
    readonly action: 'start_outreach_for_shifts';
    readonly id: string;
    readonly widget_id: string;
    readonly source_tool_call_event_id: string;
    readonly source_call_id: string;
    readonly shift_ids: Array<string>;
    readonly suggested_caregiver_ids?: Array<string> | null;
    readonly expected: DynamicWidgetExpectedState;
    readonly display_text: string;
};

// This mirrors the generated API maximum and the outreach tool's hard limit.
export const START_OUTREACH_MAX_SHIFT_COUNT = 21;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringArray(value: unknown): Array<string> | null {
    return Array.isArray(value) &&
        value.every((item): item is string => typeof item === 'string')
        ? value
        : null;
}

function getStringRecord(value: unknown): Record<string, string> | null {
    if (!isRecord(value)) {
        return null;
    }
    const entries = Object.entries(value);
    if (
        entries.length === 0 ||
        entries.some(
            ([key, item]) => key.length === 0 || typeof item !== 'string',
        )
    ) {
        return null;
    }
    return Object.fromEntries(entries) as Record<string, string>;
}

export function parseDynamicWidgetActionRequest(
    value: unknown,
): DynamicWidgetActionRequest | null {
    if (
        !isRecord(value) ||
        value['action'] !== 'start_outreach_for_shifts' ||
        typeof value['id'] !== 'string' ||
        typeof value['widget_id'] !== 'string' ||
        typeof value['source_tool_call_event_id'] !== 'string' ||
        typeof value['source_call_id'] !== 'string' ||
        typeof value['display_text'] !== 'string' ||
        !isRecord(value['expected'])
    ) {
        return null;
    }
    const shiftIds = getStringArray(value['shift_ids']);
    const rawShiftStatusesById = value['expected']['shift_statuses_by_id'];
    const rawLegacyShiftStatuses = value['expected']['shift_statuses'];
    const shiftStatusesById =
        rawShiftStatusesById === undefined || rawShiftStatusesById === null
            ? null
            : getStringRecord(rawShiftStatusesById);
    const legacyShiftStatuses =
        rawLegacyShiftStatuses === undefined || rawLegacyShiftStatuses === null
            ? null
            : getStringArray(rawLegacyShiftStatuses);
    const rawSuggestedCaregiverIds = value['suggested_caregiver_ids'];
    const suggestedCaregiverIds =
        rawSuggestedCaregiverIds === undefined ||
        rawSuggestedCaregiverIds === null
            ? null
            : getStringArray(rawSuggestedCaregiverIds);
    if (
        shiftIds === null ||
        (shiftStatusesById === null) === (legacyShiftStatuses === null) ||
        (suggestedCaregiverIds === null &&
            rawSuggestedCaregiverIds !== undefined &&
            rawSuggestedCaregiverIds !== null)
    ) {
        return null;
    }
    if (
        shiftStatusesById !== null &&
        (shiftIds.length > START_OUTREACH_MAX_SHIFT_COUNT ||
            new Set(shiftIds).size !== shiftIds.length ||
            Object.keys(shiftStatusesById).length !== shiftIds.length ||
            !shiftIds.every((shiftId) => shiftId in shiftStatusesById))
    ) {
        return null;
    }
    const expected: DynamicWidgetExpectedState | null =
        shiftStatusesById !== null
            ? { shift_statuses_by_id: shiftStatusesById }
            : legacyShiftStatuses !== null
              ? { shift_statuses: legacyShiftStatuses }
              : null;
    if (expected === null) {
        return null;
    }
    return {
        action: 'start_outreach_for_shifts',
        id: value['id'],
        widget_id: value['widget_id'],
        source_tool_call_event_id: value['source_tool_call_event_id'],
        source_call_id: value['source_call_id'],
        shift_ids: shiftIds,
        ...(suggestedCaregiverIds === null
            ? {}
            : { suggested_caregiver_ids: suggestedCaregiverIds }),
        expected,
        display_text: value['display_text'],
    };
}
